// Customer View
// Handles registration and waiting view logic

import { createCustomer, getCustomerPosition, getQueueCounts } from '../services/queue-service.js';
import { calculateWaitTime } from '../utils/time-calculator.js';
import { validatePhone, formatPhoneInput, preventPrefixDeletion } from '../utils/validators.js';
import { showSurveyViewWithId } from '../core/router.js';
import { STATUS, PHONE_CONSTANTS } from '../config/constants.js';
import stateManager from '../core/state-manager.js';

/**
 * Initialize registration form
 *
 * @param {function} showView - Function to show a view
 * @param {function} startQueueListeners - Function to start queue listeners
 */
export function initRegistrationForm(showView, startQueueListeners) {
    const form = document.getElementById('register-form');
    const phoneInput = document.getElementById('phone');

    // Phone input validation
    phoneInput.addEventListener('input', (e) => {
        e.target.value = formatPhoneInput(e.target.value);
    });

    phoneInput.addEventListener('keydown', (e) => {
        preventPrefixDeletion(e, phoneInput);
    });

    // Form submission
    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const customerData = {
            firstName: form.firstName.value,
            lastName: form.lastName.value,
            email: form.email.value,
            phone: form.phone.value,
            attentionType: form.attentionType.value,
            isPreferential: document.getElementById('isPreferential').value === 'true'
        };

        // Validate phone
        if (!validatePhone(customerData.phone)) {
            alert('Por favor, ingresa un número de teléfono válido (formato: +569XXXXXXXX)');
            return;
        }

        try {
            const customerId = await createCustomer(customerData);
            stateManager.setCurrentCustomerId(customerId);
            showView('waiting');
            startQueueListeners();
        } catch (error) {
            console.error("Error creating customer:", error);
            alert("Hubo un error al registrarte. Por favor, intenta de nuevo.");
        }
    });
}

/**
 * Update customer waiting view with real-time data
 *
 * @param {array} allCustomers - All customers from Firestore
 */
export function updateCustomerWaitingView(allCustomers) {
    const currentCustomerId = stateManager.getState('currentCustomerId');

    if (!currentCustomerId) return;

    const myData = allCustomers.find(c => c.id === currentCustomerId);

    if (!myData) {
        // Customer not found, clear and redirect
        stateManager.clearCurrentCustomerId();

        if (!window.location.search.includes('survey_id')) {
            window.location.href = window.location.pathname;
        }
        return;
    }

    // Handle different statuses
    if (myData.status === STATUS.WAITING || myData.status === STATUS.STANDBY) {
        updateWaitingStatus(myData, allCustomers);
    } else if (myData.status === STATUS.CALLING || myData.status === STATUS.SERVING) {
        updateCallingStatus(myData);
    } else if (myData.status === STATUS.DONE) {
        stateManager.clearCurrentCustomerId();
        showSurveyViewWithId(myData.id);
    }
}

/**
 * Update waiting status display
 *
 * @param {object} myData - Current customer data
 * @param {array} allCustomers - All customers
 */
function updateWaitingStatus(myData, allCustomers) {
    const sellersData = stateManager.getState('sellersData');

    // Calculate position and wait time (only for waiting customers)
    const myPosition = getCustomerPosition(myData.id, allCustomers);
    const counts = getQueueCounts(allCustomers);
    const estimatedWaitTime = calculateWaitTime(myPosition, sellersData);

    // Update display
    document.getElementById('customer-position').textContent = `#${myPosition}`;
    document.getElementById('queue-size').textContent = counts.totalWaiting;
    document.getElementById('wait-time').textContent = estimatedWaitTime.toString();

    // Hide turn notification
    document.getElementById('turn-notification').classList.add('hidden');

    // Show standby message if applicable
    if (myData.status === STATUS.STANDBY) {
        const turnNotification = document.getElementById('turn-notification');
        document.getElementById('turn-notification-text').textContent =
            'Tu turno fue llamado. Por favor, acércate a una vendedora para ser reactivado en la fila.';
        turnNotification.classList.remove('hidden');
    }
}

/**
 * Update calling/serving status display
 *
 * @param {object} myData - Current customer data
 */
function updateCallingStatus(myData) {
    document.getElementById('customer-position').textContent = '¡TÚ!';
    document.getElementById('wait-time').textContent = 'Ahora';

    const sellerName = myData.calledByName || 'una vendedora';
    document.getElementById('turn-notification-text').textContent =
        `Por favor, acércate a ${sellerName} para ser atendido.`;
    document.getElementById('turn-notification').classList.remove('hidden');
}
